import type { MouseEvent } from "react";
import Link from "next/link";
import { capitalizeFirstLetter } from "utils/utils";
import { FinishEnum, GameCard, NONFOIL_FINISH } from "modules/cards/model/card";
import { useCardsStore } from "modules/cards/stores/useCardsStore";
import { SetIcon, SetPromoIndicator } from "modules/common/components/SetIcon";
import { useSets } from "modules/common/hooks/useSets";
import { CardPrimaryPrice } from "modules/cards/components/CardPrimaryPrice";
import { primaryAvailableFinish } from "modules/cards/model/finishBehavior";
import { getCatalogueSetCode } from "modules/common/gameRegistry";

interface Props {
  card: GameCard;
  finish?: FinishEnum;
  /**
   * Hide the price slot, keeping set identity + rarity only. Used where a
   * separate per-finish price row (CardFinishPrices) renders the prices.
   */
  hidePrice?: boolean;
  /**
   * Called when the set link navigates. Hosts that embed this component in
   * their own modal (edit trade item, change printing) close it here so it
   * doesn't linger in its store as open after the page changes.
   */
  onNavigate?: () => void;
}

export function CardSetPrice({
  card,
  finish,
  hidePrice = false,
  onNavigate,
}: Props) {
  const displayFinish =
    finish ?? primaryAvailableFinish(card.finishes) ?? NONFOIL_FINISH;
  const { getSetName } = useSets();
  const closeModal = useCardsStore((state) => state.closeModal);
  const game = card.game;
  const hasKnownSet = Boolean(card.setCode || card.set?.code);
  const setCode = card.setCode || card.set?.code || "unknown";
  const catalogueSetCode = getCatalogueSetCode(game, card);
  const setName =
    card.set?.name || getSetName(catalogueSetCode, game) || setCode;
  const isPromo = card.printing?.isPromo === true;
  const number = card?.number;
  const rarity =
    card.rarity === "special" ? "timeshifted" : (card.rarity as string);

  const handleSetLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Modifier-clicks open a new tab — leave this tab's modals alone.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // Close the card modal when navigating from within it; no-op on the
    // card details page where the modal isn't open.
    closeModal();
    onNavigate?.();
  };

  const setIcon = (
    <SetPromoIndicator isPromo={isPromo}>
      <SetIcon
        setCode={setCode}
        game={game}
        size="2x"
        className="text-secondary"
        set={card.set}
      />
    </SetPromoIndicator>
  );

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          <div className="tooltip" data-tip={setName || setCode}>
            {hasKnownSet ? (
              <Link
                href={`/sets/${game}/${catalogueSetCode.toLowerCase()}`}
                onClick={handleSetLinkClick}
                aria-label={`View ${setName || setCode} set page`}
              >
                {setIcon}
              </Link>
            ) : (
              setIcon
            )}
          </div>
        </div>
        <div className="min-w-0 flex-grow">
          <div
            className="tooltip block w-full min-w-0"
            data-tip={setName || setCode}
          >
            <p className="w-full max-w-[170px] truncate text-left text-sm font-medium">
              {setName || setCode}
            </p>
          </div>
          <p className="text-secondary text-xs">
            {setCode} #{number}
          </p>
        </div>
        <div className="flex-shrink-0">
          <div className="flex flex-col items-end">
            {!hidePrice && (
              <CardPrimaryPrice
                card={card}
                className="whitespace-nowrap text-sm font-medium"
                finish={displayFinish}
                // Open toward the modal body; a top tooltip on this right-edge
                // slot overflows the dialog. overflow-clip on the row is not an
                // option — it also clips the set-name tooltips.
                tooltipPlacement="left"
              />
            )}
            <p className="text-secondary text-right text-xs">
              {rarity && capitalizeFirstLetter(rarity)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
